import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { Upload, FolderSync, Settings, HardDrive, Cloud, Clock, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, useRef, useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { useClientProject } from '@/contexts'
import { authService } from '@/services/authService'
import { getProjectAccessLevel } from '@/services/userManagementService'
import { isGlobalAdmin } from '@/types'
import {
  getRecentUploads,
  type RecentUpload,
  type ManagementAccountCategory,
} from '@/services/projectService'
import { uploadContractsTracked, uploadManagementAccountsTracked } from '@/services/uploadWithProgress'
import { CONTRACT_CATEGORIES, toCategoryValue } from '@/lib/contractCategories'
import { flattenFolderTree, folderTreePrefix, type FlatFolderNode } from '@/services/folderService'
import {
  subscribeToBatches,
  getAllBatchSnapshots,
  isTerminalStatus,
  type UploadItem,
} from '@/services/uploadBatchStore'
import { UploadInProgressPanel } from '@/components/UploadInProgressPanel'
import '@/styles/pages.css'

export const Route = createFileRoute('/_app/organization/vault')({
  head: () => ({ meta: [{ title: 'Organization Vault · AERRIS' }] }),
  // The Data Center is project-scoped and admin-only (mirrors its nav visibility): it requires an
  // open project, and the caller must be a global admin or an admin of that project. Enforced here
  // before render so it can't be reached by direct URL. Non-qualifying callers fall back to the
  // agreements vault, which scopes them to what they can access.
  beforeLoad: async ({ search }) => {
    const session = await authService.ensureSession()
    // Not signed in / server unreachable: let the app shell (_app) and the API 401 handler decide.
    if (session.status !== 'authenticated') return
    const { projectId } = search
    if (!projectId) throw redirect({ to: '/law/vault', search: true, replace: true })
    if (isGlobalAdmin(session.user.role)) return
    // The access-level endpoint resolves inheritance, so a client admin of the project's client
    // also reads as ADMIN here. A 403 (no access at all) is caught and treated as "not admin".
    const level = await getProjectAccessLevel(projectId).catch(() => null)
    if (level !== 'ADMIN') throw redirect({ to: '/law/vault', search: true, replace: true })
  },
  component: OrganizationVaultPage,
})

// Obscures the specifics of the mock VDR connections: blurred, unselectable, and inert to hover so
// the sample text can't be read, copied, or mistaken for a real connection.
const VDR_BLUR: React.CSSProperties = {
  filter: 'blur(4px)',
  userSelect: 'none',
  pointerEvents: 'none',
}

const mockVdrConnections = [
  { id: '1', name: 'Intralinks - Project Alpha', status: 'pending', lastSync: '5 min ago', documents: 156 },
  { id: '2', name: 'Box - Due Diligence Room', status: 'pending', lastSync: '1 hour ago', documents: 89 },
  { id: '3', name: 'SFTP - Legacy Archive', status: 'pending', lastSync: '3 days ago', documents: 432 },
]

/** One Recent Uploads row: a live SSE-tracked file or a persisted document. */
interface RecentUploadRow {
  key: string
  /** Persisted document uuid, for the name/analysis link. Null for in-progress live rows. */
  uuid: string | null
  kind: 'CONTRACT' | 'MANAGEMENT_ACCOUNT' | null
  name: string
  category: string
  /** Folder the contract sits in, or null (project root, or a management account). */
  folderId: string | null
  uploadedAt: string | null
  status: 'queued' | 'processing' | 'loaded' | 'skipped' | 'failed'
  detail?: string | null
}

const PAGE_SIZE = 10

const STATUS_LABEL: Record<RecentUploadRow['status'], string> = {
  queued: 'Queued',
  processing: 'Processing...',
  loaded: 'Loaded',
  skipped: 'Skipped',
  failed: 'Failed',
}

function liveItemStatus(item: UploadItem): RecentUploadRow['status'] {
  switch (item.status) {
    case 'QUEUED': return 'queued'
    case 'PROCESSING': return 'processing'
    case 'COMPLETED': return 'loaded'
    case 'SKIPPED': return 'skipped'
    default: return 'failed'
  }
}

function formatCategory(category: string | null): string {
  if (!category) return '—'
  if (category === 'managementAccount') return 'Management account'
  return category.charAt(0).toUpperCase() + category.slice(1)
}

// The document-type selector, split into three visual sections (native <optgroup>s): auto-detect,
// the contract Categories, and per-category management accounts. Options carry an explicit value so
// the two pipelines can be distinguished: contract options (and "Detect") use their label as the
// value — toCategoryValue collapses "Detect" to 'detect' — while each management-account option uses
// a `ma:`-prefixed value (`ma:client`, …) that managementAccountCategory() decodes to the bare
// category the server expects. The shared CONTRACT_CATEGORIES is the single source of truth for the
// middle group. NB: keep "Detect" a single word — toCategoryValue relies on it.
const MA_OPTION_PREFIX = 'ma:'

const CATEGORY_OPTION_GROUPS: { label: string; options: { value: string; label: string }[] }[] = [
  { label: 'Auto-detect', options: [{ value: 'Detect', label: 'Detect' }] },
  { label: 'Contract type', options: CONTRACT_CATEGORIES.map((c) => ({ value: c, label: c })) },
  {
    label: 'Management account',
    options: [
      { value: `${MA_OPTION_PREFIX}client`, label: 'Management account · Client' },
      { value: `${MA_OPTION_PREFIX}lease`, label: 'Management account · Lease' },
      { value: `${MA_OPTION_PREFIX}employee`, label: 'Management account · Employee' },
      { value: `${MA_OPTION_PREFIX}supplier`, label: 'Management account · Supplier' },
    ],
  },
]
// "Detect" is the default: uploads auto-detect their contract type unless the user picks otherwise.
const DEFAULT_DOCUMENT_TYPE = 'Detect'

/** The management-account category (client/lease/employee/supplier) a documentType selects, or null
 *  when the selected type is a contract Category / Detect (i.e. the standard contract pipeline). */
function managementAccountCategory(documentType: string): ManagementAccountCategory | null {
  return documentType.startsWith(MA_OPTION_PREFIX)
    ? (documentType.slice(MA_OPTION_PREFIX.length) as ManagementAccountCategory)
    : null
}

// Shared style for the inline management-account field captions (period start/end, label).
const MA_FIELD_LABEL_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontSize: '0.75rem',
  color: 'var(--text-secondary, #6b7280)',
}

function OrganizationVaultPage() {
  const { clientId, projectId } = useClientProject()
  const [documentType, setDocumentType] = useState<string>(DEFAULT_DOCUMENT_TYPE)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Folder destination for contract uploads ('' = project root). Folders don't apply to
  // management accounts, so the selector is hidden for that document type.
  const [folders, setFolders] = useState<FlatFolderNode[]>([])
  const [selectedFolderId, setSelectedFolderId] = useState<string>('')
  // Management-account reporting period + optional label. Revealed only for management-account
  // document types; the period is required (end on/after start) before the upload is allowed.
  const [periodStart, setPeriodStart] = useState<string>('')
  const [periodEnd, setPeriodEnd] = useState<string>('')
  const [maLabel, setMaLabel] = useState<string>('')
  const maCategory = managementAccountCategory(documentType)
  const isManagementAccount = maCategory != null
  const periodValid = periodStart !== '' && periodEnd !== '' && periodEnd >= periodStart

  // Load the project's folder tree (flattened, with tree-line guides) for the destination selector.
  useEffect(() => {
    if (!projectId) {
      setFolders([])
      setSelectedFolderId('')
      return
    }
    let cancelled = false
    const load = async () => {
      let flat: FlatFolderNode[] = []
      try { flat = await flattenFolderTree(projectId) } catch { /* leave folders empty on failure */ }
      if (!cancelled) {
        setFolders(flat)
        setSelectedFolderId('')
      }
    }
    load()
    return () => { cancelled = true }
  }, [projectId])

  // ---- Recent uploads: server-paged persisted rows + live overlay from the SSE batch store ----
  // Server-side paging (10/page). Live in-progress rows are pinned to the top of page 1 only; deeper
  // pages show server rows alone. Page resets to the first page whenever the project changes.
  const [persistedUploads, setPersistedUploads] = useState<RecentUpload[]>([])
  const [uploadsTotal, setUploadsTotal] = useState(0)
  const [uploadsPage, setUploadsPage] = useState(0)
  const liveBatches = useSyncExternalStore(subscribeToBatches, getAllBatchSnapshots)

  useEffect(() => { setUploadsPage(0) }, [projectId])

  // Fast lookup of a folder's display path from the already-loaded flattened tree.
  const foldersByUuid = useMemo(() => {
    const m = new Map<string, FlatFolderNode>()
    folders.forEach((n) => m.set(n.folder.uuid, n))
    return m
  }, [folders])

  // Build a "Parent / Child" label for a folder by walking parentId up the loaded tree.
  const folderLabel = useCallback((folderId: string): string => {
    const names: string[] = []
    const seen = new Set<string>()
    let cursor: string | null = folderId
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor)
      const node = foldersByUuid.get(cursor)
      if (!node) break
      names.unshift(node.folder.name)
      cursor = node.folder.parentId ?? null
    }
    return names.length ? names.join(' / ') : 'Folder'
  }, [foldersByUuid])

  // Refetch the current page when the project or page changes, and whenever a batch settles (so
  // freshly processed documents move from the live overlay into the persisted rows on page 1).
  const completedBatchCount = liveBatches.filter((b) => b.completed).length
  useEffect(() => {
    if (!projectId) {
      setPersistedUploads([])
      setUploadsTotal(0)
      return
    }
    let cancelled = false
    getRecentUploads(projectId, uploadsPage * PAGE_SIZE, PAGE_SIZE)
      .then((page) => {
        if (cancelled) return
        setPersistedUploads(page.rows)
        setUploadsTotal(page.total)
      })
      .catch((err) => console.warn('Failed to fetch recent uploads:', err))
    return () => { cancelled = true }
  }, [projectId, uploadsPage, completedBatchCount])

  const recentUploadRows = useMemo<RecentUploadRow[]>(() => {
    const persisted: RecentUploadRow[] = persistedUploads.map((p) => ({
      key: `persisted-${p.uuid}`,
      uuid: p.uuid,
      kind: p.kind,
      name: p.name || 'Untitled',
      category: formatCategory(p.category),
      folderId: p.folderId,
      uploadedAt: p.createdAt,
      status: 'loaded' as const,
    }))

    // Live rows are client-side and unpersisted, so they only make sense on the first page.
    if (uploadsPage !== 0) return persisted

    const liveItems = liveBatches.flatMap((b) => b.items)
    // A successfully loaded live item reappears in the persisted refetch under its extracted
    // document name — drop the live row once that happens. Failed and skipped rows stay visible
    // so the outcome isn't silently lost.
    const live: RecentUploadRow[] = liveItems
      .filter((i) => !(
        isTerminalStatus(i.status)
        && i.contractUuid != null
        && persistedUploads.some((p) => p.uuid === i.contractUuid)
      ))
      .map((i) => ({
        key: `live-${i.itemId}`,
        uuid: null,
        kind: null,
        name: i.fileName || 'Unnamed file',
        category: '—',
        folderId: null,
        uploadedAt: null,
        status: liveItemStatus(i),
        detail: i.detail,
      }))
      .reverse() // newest batch items first

    return [...live, ...persisted]
  }, [liveBatches, persistedUploads, uploadsPage])

  const pageCount = Math.max(1, Math.ceil(uploadsTotal / PAGE_SIZE))

  const handleUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return
    if (!projectId) {
      setUploadStatus({ type: 'error', message: 'Please select a client and project before uploading.' })
      return
    }

    const maCat = managementAccountCategory(documentType)
    // Management accounts require a valid reporting period (end on/after start) before uploading.
    if (maCat && !(periodStart !== '' && periodEnd !== '' && periodEnd >= periodStart)) {
      setUploadStatus({ type: 'error', message: 'Set a period start and end (end on or after start) before uploading a management account.' })
      return
    }

    setIsUploading(true)
    setUploadStatus(null)

    try {
      if (maCat) {
        // A management account is a single CSV; the server mints the batchId and streams its
        // one-item progress over SSE just like a contract batch.
        await uploadManagementAccountsTracked(
          projectId,
          files[0],
          maCat,
          periodStart,
          periodEnd,
          maLabel.trim() || null,
        )
        setUploadStatus({ type: 'success', message: 'Management account uploaded — processing in the background.' })
      } else {
        const category = toCategoryValue(documentType)
        // Tracked: drives the two-phase upload toast (byte upload → background processing).
        await uploadContractsTracked(projectId, Array.from(files), category, selectedFolderId || null)
        setUploadStatus({ type: 'success', message: `All ${files.length} file(s) uploaded — processing in the background.` })
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.'
      setUploadStatus({ type: 'error', message })
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [projectId, documentType, selectedFolderId, periodStart, periodEnd, maLabel])

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUpload(e.target.files)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    handleUpload(e.dataTransfer.files)
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const triggerFileSelect = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Organization Vault</h1>
          <p className="page-subtitle">Upload files and manage Virtual Data Room connections</p>
        </div>
      </div>

      <div
        className="upload-zone"
        onDrop={onDrop}
        onDragOver={onDragOver}
        style={{ position: 'relative' }}
      >
        <div className="upload-zone-content">
          <Upload size={32} strokeWidth={1.5} />
          <h3>Drop files here to upload</h3>
          <p>or click to browse your computer</p>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <select
              className="filter-select"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              style={{ minWidth: '180px' }}
            >
              {CATEGORY_OPTION_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {!isManagementAccount && (
              <select
                className="filter-select"
                value={selectedFolderId}
                onChange={(e) => setSelectedFolderId(e.target.value)}
                style={{ minWidth: '180px' }}
                title="Destination folder"
              >
                <option value="">Project root</option>
                {folders.map((node) => (
                  <option key={node.folder.uuid} value={node.folder.uuid}>
                    {folderTreePrefix(node) + node.folder.name}
                  </option>
                ))}
              </select>
            )}
            {isManagementAccount && (
              <>
                <label style={MA_FIELD_LABEL_STYLE}>
                  Period start
                  <input
                    type="date"
                    className="filter-select"
                    value={periodStart}
                    max={periodEnd || undefined}
                    onChange={(e) => setPeriodStart(e.target.value)}
                  />
                </label>
                <label style={MA_FIELD_LABEL_STYLE}>
                  Period end
                  <input
                    type="date"
                    className="filter-select"
                    value={periodEnd}
                    min={periodStart || undefined}
                    onChange={(e) => setPeriodEnd(e.target.value)}
                  />
                </label>
                <label style={MA_FIELD_LABEL_STYLE}>
                  Label (optional)
                  <input
                    type="text"
                    className="filter-select"
                    value={maLabel}
                    placeholder="e.g. FY24"
                    onChange={(e) => setMaLabel(e.target.value)}
                  />
                </label>
              </>
            )}
            <button
              className="btn btn-primary"
              onClick={triggerFileSelect}
              disabled={isUploading || (isManagementAccount && !periodValid)}
            >
              {isUploading ? (
                <Loader2 size={16} className="loading-spinner" />
              ) : (
                <Upload size={16} />
              )}
              {isUploading ? 'Uploading...' : 'Select Files'}
            </button>
          </div>
          {uploadStatus && (
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.5rem 0.75rem',
                borderRadius: 'var(--radius-control)',
                fontSize: '0.875rem',
                color: uploadStatus.type === 'success' ? 'var(--success, #059669)' : 'var(--danger, #dc2626)',
                backgroundColor: uploadStatus.type === 'success' ? 'var(--success-bg, #d1fae5)' : 'var(--danger-bg, #fee2e2)',
              }}
            >
              {uploadStatus.message}
            </div>
          )}
          {!projectId && (
            <div
              style={{
                marginTop: '0.75rem',
                fontSize: '0.875rem',
                color: 'var(--text-muted, #6b7280)',
              }}
            >
              Select a client and project to enable uploads.
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple={!isManagementAccount}
          accept={isManagementAccount ? '.csv,text/csv' : undefined}
          style={{ display: 'none' }}
          onChange={onFileInputChange}
        />
      </div>

      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Virtual Data Room Connections</h2>
          <button className="btn btn-secondary">
            <FolderSync size={16} />
            Add Connection
          </button>
        </div>
        <div className="vdr-grid">
          {mockVdrConnections.map((vdr) => (
            <div key={vdr.id} className={`vdr-card ${vdr.status}`}>
              <div className="vdr-header">
                <div className="vdr-icon">
                  <Cloud size={20} />
                </div>
                <div className="vdr-status">
                  <Clock size={16} className="status-icon pending" />
                  <span>{vdr.status}</span>
                </div>
              </div>
              {/* These connections are mock/illustrative only — blur the specifics so they can't be
                  mistaken for real, live data, while still conveying the card layout. */}
              <h3 style={VDR_BLUR}>{vdr.name}</h3>
              <div className="vdr-meta">
                <span><HardDrive size={14} /> <span style={VDR_BLUR}>{vdr.documents} documents</span></span>
                <span style={VDR_BLUR}>Last sync: {vdr.lastSync}</span>
              </div>
              <div className="vdr-actions">
                <button className="btn btn-sm btn-secondary">
                  <FolderSync size={14} />
                  Sync Now
                </button>
                <button className="btn btn-sm btn-ghost">
                  <Settings size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <UploadInProgressPanel />

      <section className="section">
        <h2 className="section-title">Recent Uploads</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Folder</th>
                <th>Category</th>
                <th>Uploaded</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentUploadRows.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ color: 'var(--text-muted, #6b7280)' }}>
                    {projectId ? 'No uploads yet for this project.' : 'Select a client and project to see uploads.'}
                  </td>
                </tr>
              )}
              {recentUploadRows.map((row) => (
                <tr key={row.key}>
                  <td>
                    {row.uuid ? (
                      <Link
                        to="/law/vault/analysis/$contractId"
                        params={{ contractId: row.uuid }}
                        search={{
                          ...(row.kind === 'MANAGEMENT_ACCOUNT' ? { kind: 'managementAccount' as const } : {}),
                        }}
                        style={{ color: 'var(--accent-ink)', textDecoration: 'none', fontWeight: 500 }}
                      >
                        {row.name}
                      </Link>
                    ) : (
                      row.name
                    )}
                  </td>
                  <td>
                    {row.uuid == null || row.kind === 'MANAGEMENT_ACCOUNT' ? (
                      <span style={{ color: 'var(--text-muted, #6b7280)' }}>—</span>
                    ) : row.folderId ? (
                      <Link
                        to="/law/vault"
                        search={{
                          clientId: clientId ?? '',
                          projectId: projectId ?? '',
                          folderId: row.folderId,
                        }}
                        style={{ color: 'var(--accent-ink)', textDecoration: 'none' }}
                      >
                        {folderLabel(row.folderId)}
                      </Link>
                    ) : (
                      <span style={{ color: 'var(--text-muted, #6b7280)' }}>Project root</span>
                    )}
                  </td>
                  <td>{row.category}</td>
                  <td>{row.uploadedAt ? new Date(row.uploadedAt).toLocaleString() : 'Just now'}</td>
                  <td>
                    <span
                      className={`status-badge ${row.status === 'loaded' ? 'complete' : row.status}`}
                      title={row.detail ?? undefined}
                    >
                      {STATUS_LABEL[row.status]}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {uploadsTotal > PAGE_SIZE && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              marginTop: '0.75rem',
              fontSize: '0.8125rem',
              color: 'var(--text-secondary, #6b7280)',
            }}
          >
            <span>Page {uploadsPage + 1} of {pageCount}</span>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setUploadsPage((p) => Math.max(0, p - 1))}
              disabled={uploadsPage === 0}
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setUploadsPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={uploadsPage >= pageCount - 1}
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </section>
    </div>
  )
}
